import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FileText, ExternalLink, Play, ListChecks, Award, Loader2, Download, BookOpen, ShoppingBag, ArrowUpRight, Package, Lock, ArrowRight, FileSpreadsheet, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { useReaderAuth, readerFetch } from "@/contexts/ReaderAuthContext";
import { BackButton } from "@/pages/extra/PublicLayout";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExtraBookPage() {
    const { bookId } = useParams();
    const { reader, checking } = useReaderAuth();
    const [book, setBook] = useState(null);
    const [contents, setContents] = useState([]);
    const [loadingBook, setLoadingBook] = useState(true);
    const [loadingContents, setLoadingContents] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [activeQuiz, setActiveQuiz] = useState(null);

    // Public book meta — always fetched (no login required for hero + CTA acquisto)
    useEffect(() => {
        (async () => {
            setLoadingBook(true);
            try {
                const res = await fetch(`${API_ROOT}/public/extra/books/${bookId}`);
                if (res.ok) {
                    const d = await res.json();
                    setBook(d.book);
                } else if (res.status === 404) {
                    setNotFound(true);
                }
            } finally {
                setLoadingBook(false);
            }
        })();
    }, [bookId]);

    // Reader-only contents — only when logged in
    useEffect(() => {
        if (!reader || !book) return;
        (async () => {
            setLoadingContents(true);
            try {
                const res = await readerFetch(`/reader/books/${bookId}`);
                if (res.ok) {
                    const d = await res.json();
                    setContents(d.contents || []);
                }
            } finally {
                setLoadingContents(false);
            }
        })();
    }, [bookId, reader, book]);

    if (loadingBook) {
        return (
            <div className="py-20 text-center text-pvx-textmuted text-[13px]">
                <Loader2 size={20} strokeWidth={1.5} className="animate-spin inline-block mr-2" />
                Carico il libro…
            </div>
        );
    }
    if (notFound || !book) {
        return (
            <div>
                <BackButton />
                <div className="pvx-card p-8 text-center text-pvx-textmuted">Libro non trovato.</div>
            </div>
        );
    }

    if (activeQuiz) {
        return (
            <div>
                <button onClick={() => setActiveQuiz(null)}
                        data-testid="quiz-back"
                        className="inline-flex items-center gap-1.5 text-[13px] text-pvx-textmuted hover:text-pvx-textmain transition-colors mb-6">
                    ← Torna al libro
                </button>
                <QuizRunner quizId={activeQuiz} bookTitle={book.title} />
            </div>
        );
    }

    const downloadFolder = async (folder) => {
        const res = await readerFetch(`/reader/download-folder/${book.id}?folder=${encodeURIComponent(folder)}`);
        if (!res.ok) { toast.error("Errore download cartella"); return; }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${folder} - ${book.title}.zip`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("Download cartella avviato");
    };

    return (
        <div data-testid="book-detail-page">
            <BackButton />

            {/* Hero — cover + intro premium (public) */}
            <div className="pvx-card overflow-hidden mb-10 md:mb-12 relative">
                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-pvx-primary/[0.04] via-transparent to-pvx-azure/[0.06]" />
                <div aria-hidden="true" className="absolute inset-0 opacity-70" style={{ backgroundImage: "radial-gradient(circle at 10% 20%, rgb(var(--pvx-rgb-primary)/0.08), transparent 55%), radial-gradient(circle at 90% 90%, rgb(var(--pvx-rgb-azure)/0.10), transparent 60%)" }} />

                <div className="relative grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-10 p-6 md:p-10 lg:p-14">
                    <div className="md:col-span-2 flex justify-center md:justify-start items-center">
                        <div className="w-full max-w-[320px] aspect-[3/4] rounded-2xl overflow-hidden flex items-center justify-center"
                             style={{ backgroundImage: "linear-gradient(180deg, #F7F9FC 0%, #EEF2F8 100%)" }}>
                            {book.cover_path ? (
                                <img src={`${API_ROOT}/public/extra/cover/${book.id}`}
                                     alt={book.title}
                                     className="max-h-[86%] max-w-[80%] object-contain drop-shadow-[0_22px_45px_rgba(52,127,184,0.28)]"
                                     data-testid={`book-detail-cover-${book.id}`}
                                     onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            ) : (
                                <BookOpen size={72} strokeWidth={1} className="text-pvx-primary/25" />
                            )}
                        </div>
                    </div>
                    <div className="md:col-span-3 flex flex-col justify-center">
                        {book.author_name && (
                            <div className="pvx-eyebrow pvx-brand-blue mb-3">Un libro di {book.author_name}</div>
                        )}
                        <h1 className="pvx-display text-[32px] md:text-[42px] lg:text-[52px] font-normal tracking-[-0.005em] leading-[1.08] mb-4">
                            {book.title}
                        </h1>
                        {book.subtitle && (
                            <div className="text-[15px] md:text-[17px] text-pvx-textmuted italic mb-6">{book.subtitle}</div>
                        )}
                        {book.long_description && (
                            <p className="text-[14px] md:text-[15px] text-pvx-textmain/85 leading-relaxed whitespace-pre-wrap mb-6">
                                {book.long_description}
                            </p>
                        )}
                        {(book.purchase_url || "").trim() && <PurchaseBar book={book} />}
                    </div>
                </div>
            </div>

            {/* Contents area */}
            <div className="mb-5 flex items-center justify-between">
                <h2 className="pvx-serif text-2xl tracking-tight">Contenuti extra collegati</h2>
                {reader && !loadingContents && (
                    <div className="text-[11.5px] text-pvx-textsubtle uppercase tracking-wider" data-testid="contents-count">
                        {contents.length} {contents.length === 1 ? "elemento" : "elementi"}
                    </div>
                )}
            </div>

            {!reader && !checking ? (
                <ContentsLoginGate bookId={book.id} />
            ) : loadingContents ? (
                <div className="pvx-card p-8 text-center text-pvx-textmuted text-[13px]">
                    <Loader2 size={16} strokeWidth={1.5} className="animate-spin inline-block mr-2" />
                    Carico i contenuti…
                </div>
            ) : contents.length === 0 ? (
                <div className="pvx-card p-10 text-center" data-testid="book-empty-contents">
                    <BookOpen size={26} strokeWidth={1.5} className="mx-auto text-pvx-textsubtle mb-3" />
                    <div className="text-[13.5px] text-pvx-textmuted">Nessun contenuto ancora pubblicato per questo libro. Torna a trovarci a breve.</div>
                </div>
            ) : (
                <div className="space-y-8">
                    {groupByFolder(contents).map((group) => (
                        <div key={group.folder || "_root"} data-testid={group.folder ? `content-folder-${group.folder}` : "content-folder-root"}>
                            {group.folder && (
                                <div className="flex items-center gap-2.5 mb-4">
                                    <div className="h-8 w-8 rounded-[10px] bg-pvx-primary/[0.08] text-pvx-primary flex items-center justify-center">
                                        <FolderOpen size={15} strokeWidth={1.5} />
                                    </div>
                                    <h3 className="pvx-serif text-lg tracking-tight">{group.folder}</h3>
                                    <div className="text-[11px] text-pvx-textsubtle uppercase tracking-wider">{group.items.length} {group.items.length === 1 ? "file" : "file"}</div>
                                    <div className="flex-1 pvx-divider" />
                                    {group.items.some(i => i.storage_path) && (
                                        <button
                                            onClick={() => downloadFolder(group.folder)}
                                            data-testid={`folder-download-${group.folder}`}
                                            className="h-8 px-3 rounded-[10px] bg-pvx-primary text-white text-[12px] font-medium hover:opacity-90 flex items-center gap-1.5 transition-opacity"
                                        >
                                            <Download size={12.5} strokeWidth={1.75} />
                                            Scarica tutta la cartella
                                        </button>
                                    )}
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                                {group.items.map((c) => (
                                    <ContentCard key={c.id} c={c} book={book} onQuizOpen={setActiveQuiz} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function groupByFolder(contents) {
    const groups = [];
    const seen = {};
    contents.forEach((c) => {
        const f = (c.folder || "").trim();
        if (!(f in seen)) {
            seen[f] = { folder: f, items: [] };
            groups.push(seen[f]);
        }
        seen[f].items.push(c);
    });
    return groups;
}

function PurchaseBar({ book }) {
    const label = (book.purchase_cta_text || "").trim() || "Acquista il libro";
    return (
        <a
            href={book.purchase_url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${label} (si apre in una nuova finestra)`}
            data-testid={`book-detail-purchase-${book.id}`}
            className="group relative inline-flex items-center gap-3 h-12 pl-5 pr-4 rounded-xl bg-pvx-primary text-white text-[14px] font-medium overflow-hidden shadow-[0_10px_25px_rgba(27,127,184,0.28)] hover:shadow-[0_15px_35px_rgba(27,127,184,0.38)] hover:-translate-y-0.5 transition-all self-start"
        >
            <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-transparent via-white/12 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            <ShoppingBag size={16} strokeWidth={1.75} className="relative" />
            <span className="relative">{label}</span>
            <ArrowUpRight size={16} strokeWidth={1.75} className="relative transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
    );
}

function ContentsLoginGate({ bookId }) {
    return (
        <div
            data-testid="contents-login-gate"
            className="pvx-card p-8 md:p-10 relative overflow-hidden"
        >
            <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-pvx-primary/[0.05] via-transparent to-pvx-azure/[0.08]" />
            <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
                <div className="h-14 w-14 shrink-0 rounded-xl bg-pvx-primary/10 text-pvx-primary flex items-center justify-center">
                    <Lock size={22} strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                    <div className="pvx-serif text-2xl md:text-3xl tracking-tight mb-2">
                        Accedi per sbloccare i contenuti extra
                    </div>
                    <p className="text-[13.5px] md:text-[14px] text-pvx-textmuted leading-relaxed max-w-lg">
                        Accedi o registrati gratuitamente per scaricare i pacchetti, aprire i quiz e ricevere tutti gli aggiornamenti collegati a questo libro.
                    </p>
                </div>
                <div className="flex gap-2 shrink-0">
                    <Link to={`/reader/login?redirect=/extra/${bookId}`}
                          data-testid="contents-gate-login"
                          className="h-11 px-4 rounded-[10px] border border-pvx-border hover:bg-pvx-surface2 flex items-center text-[13.5px] font-medium transition-colors">
                        Accedi
                    </Link>
                    <Link to={`/reader/register?redirect=/extra/${bookId}`}
                          data-testid="contents-gate-register"
                          className="h-11 px-4 rounded-[10px] bg-pvx-primary text-white hover:opacity-90 flex items-center gap-2 text-[13.5px] font-medium transition-opacity">
                        Registrati <ArrowRight size={13.5} strokeWidth={1.75} />
                    </Link>
                </div>
            </div>
        </div>
    );
}

const KIND_META = {
    pdf:        { Icon: FileText,        ctaLabel: "Scarica il PDF",        eyebrow: "Documento PDF" },
    zip:        { Icon: Package,         ctaLabel: "Scarica ora",           eyebrow: "Pacchetto scaricabile" },
    image:      { Icon: FileText,        ctaLabel: "Scarica",               eyebrow: "Documento" },
    excel:      { Icon: FileSpreadsheet, ctaLabel: "Scarica il file Excel", eyebrow: "Foglio Excel" },
    link:       { Icon: ExternalLink,    ctaLabel: "Apri link",             eyebrow: "Risorsa esterna" },
    video:      { Icon: Play,            ctaLabel: "Guarda il video",       eyebrow: "Video" },
    embed:      { Icon: Play,            ctaLabel: "Guarda",                eyebrow: "Contenuto embed" },
    checklist:  { Icon: ListChecks,      ctaLabel: "Apri la checklist",     eyebrow: "Checklist" },
    quiz:       { Icon: Award,           ctaLabel: "Inizia il quiz",        eyebrow: "Quiz interattivo" },
};

function ContentCard({ c, book, onQuizOpen }) {
    const meta = KIND_META[c.kind] || KIND_META.image;
    const Icon = meta.Icon;

    const download = async () => {
        const res = await readerFetch(`/reader/download/${c.id}`);
        if (!res.ok) { toast.error("Errore download"); return; }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = c.original_filename || `${c.title || "download"}.zip`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("Download avviato");
    };

    const open = async () => {
        if (c.kind === "quiz" && c.quiz_id) { onQuizOpen(c.quiz_id); return; }
        if (c.kind === "link" && c.url) {
            await readerFetch("/reader/track", {
                method: "POST",
                body: JSON.stringify({ event: "link_opened", book_id: book.id, book_title: book.title, content_id: c.id, content_title: c.title }),
            });
            window.open(c.url, "_blank", "noopener,noreferrer");
            return;
        }
        if (c.storage_path) { await download(); return; }
    };

    const primaryTone = c.kind === "quiz" ? "bg-pvx-primary text-white hover:opacity-90 shadow-[0_10px_20px_rgba(27,127,184,0.22)]" :
                        c.kind === "zip" ? "bg-pvx-primary text-white hover:opacity-90 shadow-[0_10px_20px_rgba(27,127,184,0.22)]" :
                        "bg-pvx-primary text-white hover:opacity-90";

    return (
        <div
            data-testid={`content-${c.id}`}
            data-content-kind={c.kind}
            className="pvx-card p-6 flex flex-col relative overflow-hidden group hover:-translate-y-0.5 hover:shadow-xl transition-all duration-300"
        >
            <div aria-hidden="true" className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-br from-pvx-primary/[0.04] to-transparent" />
            <div className="relative flex items-start gap-4 mb-4">
                <ContentThumb c={c} Icon={Icon} />
                <div className="flex-1 min-w-0">
                    <div className="pvx-eyebrow text-pvx-textsubtle mb-1">{meta.eyebrow}</div>
                    <div className="text-[15px] font-medium leading-snug">{c.title}</div>
                </div>
            </div>

            {c.content_text && (
                <p className="relative text-[13px] text-pvx-textmain/80 line-clamp-3 mb-4 leading-relaxed">
                    {c.content_text}
                </p>
            )}

            {(c.kind === "video" || c.kind === "embed") && c.url && (
                <div className="relative aspect-video rounded-xl overflow-hidden bg-black mb-4">
                    <iframe title={c.title} src={c.url} className="w-full h-full" allowFullScreen />
                </div>
            )}

            <button
                onClick={open}
                data-testid={`content-open-${c.id}`}
                className={`relative mt-auto inline-flex items-center justify-center gap-2 h-11 px-4 rounded-[10px] text-[13.5px] font-medium transition-all ${primaryTone}`}
            >
                {c.kind === "quiz" ? <Award size={15} strokeWidth={1.75} /> :
                 c.kind === "link" ? <ExternalLink size={15} strokeWidth={1.75} /> :
                 c.kind === "zip" ? <Package size={15} strokeWidth={1.75} /> :
                 <Download size={15} strokeWidth={1.75} />}
                {meta.ctaLabel}
                <ArrowRight size={14} strokeWidth={1.75} className="transition-transform group-hover:translate-x-0.5" />
            </button>
        </div>
    );
}

function ContentThumb({ c, Icon }) {
    const [err, setErr] = useState(false);
    const hasPreview = !err && c.storage_path && (c.kind === "pdf" || c.kind === "excel");
    if (hasPreview) {
        return (
            <div className="h-[84px] w-16 shrink-0 rounded-lg overflow-hidden border border-pvx-border/[0.12] bg-white shadow-sm">
                <img
                    src={`${API_ROOT}/public/extra/preview/${c.id}`}
                    alt={`Anteprima ${c.title}`}
                    loading="lazy"
                    onError={() => setErr(true)}
                    data-testid={`content-preview-${c.id}`}
                    className="w-full h-full object-cover object-top"
                />
            </div>
        );
    }
    return (
        <div className="h-12 w-12 shrink-0 rounded-xl bg-pvx-primary/[0.08] text-pvx-primary flex items-center justify-center">
            <Icon size={20} strokeWidth={1.5} />
        </div>
    );
}

function QuizRunner({ quizId, bookTitle }) {
    const [quiz, setQuiz] = useState(null);
    const [answers, setAnswers] = useState([]);
    const [result, setResult] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        (async () => {
            const res = await readerFetch(`/reader/quiz/${quizId}`);
            if (res.ok) {
                const q = await res.json();
                setQuiz(q);
                setAnswers(q.questions.map(() => -1));
            }
        })();
    }, [quizId]);

    if (!quiz) return <div className="text-pvx-textmuted"><Loader2 size={16} strokeWidth={1.5} className="inline-block animate-spin mr-2" />Carico quiz…</div>;

    const submit = async () => {
        if (answers.some(a => a < 0)) { toast.error("Rispondi a tutte le domande"); return; }
        setSubmitting(true);
        try {
            const res = await readerFetch(`/reader/quiz/${quizId}/submit`, {
                method: "POST", body: JSON.stringify({ answers }),
            });
            if (!res.ok) throw new Error("Errore invio");
            setResult(await res.json());
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (result) {
        return (
            <div className="pvx-card p-6 md:p-8" data-testid="quiz-result">
                <div data-testid="quiz-result-status" className={`text-2xl pvx-serif tracking-tight mb-2 ${result.passed ? "text-pvx-success" : "text-pvx-warning"}`}>
                    {result.passed ? "Complimenti, hai superato il quiz!" : "Quasi! Ripassa e riprova."}
                </div>
                <div data-testid="quiz-result-score" className="text-[13px] text-pvx-textmuted mb-6">
                    Punteggio: <b>{result.score}%</b> ({result.correct}/{result.total} risposte corrette)
                </div>
                <div className="space-y-4">
                    {result.review.map((r, i) => (
                        <div key={i} className="border-t border-pvx-border/[0.08] pt-3">
                            <div className="text-[13px] font-medium mb-2">{i + 1}. {r.q}</div>
                            <div className={`text-[12.5px] ${r.your === r.correct_index ? "text-pvx-success" : "text-pvx-critical"}`}>
                                {r.your === r.correct_index ? "✓ Corretto" : "✗ Errato"}
                            </div>
                            {r.explain && <div className="text-[12px] text-pvx-textmuted mt-1">{r.explain}</div>}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="pvx-card p-6 md:p-8" data-testid="quiz-runner">
            <div className="pvx-eyebrow text-pvx-primary mb-2">Quiz · {bookTitle}</div>
            <h2 className="pvx-serif text-3xl tracking-tight mb-2">{quiz.title}</h2>
            {quiz.description && <p className="text-[13.5px] text-pvx-textmuted mb-6">{quiz.description}</p>}

            <div className="space-y-6">
                {quiz.questions.map((qs, i) => (
                    <div key={i}>
                        <div className="text-[13.5px] font-medium mb-2">{i + 1}. {qs.q}</div>
                        <div className="grid grid-cols-1 gap-1.5">
                            {qs.options.map((opt, j) => (
                                <label key={j} data-testid={`quiz-q${i}-opt${j}`}
                                    onClick={() => setAnswers(a => a.map((v, k) => k === i ? j : v))}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-[10px] border cursor-pointer transition-colors ${
                                        answers[i] === j ? "border-pvx-primary bg-pvx-primary/[0.06]" : "border-pvx-border hover:bg-pvx-surface2"
                                    }`}>
                                    <input
                                        type="radio"
                                        name={`q${i}`}
                                        checked={answers[i] === j}
                                        onChange={() => setAnswers(a => a.map((v, k) => k === i ? j : v))}
                                        className="accent-pvx-primary"
                                    />
                                    <span className="text-[13px]">{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <button
                onClick={submit}
                disabled={submitting}
                data-testid="quiz-submit"
                className="mt-8 h-10 px-5 rounded-[10px] bg-pvx-primary text-white hover:opacity-90 disabled:opacity-60 text-[13px] font-medium transition-opacity flex items-center gap-2"
            >
                {submitting ? "Invio…" : "Invia risposte"}
            </button>
        </div>
    );
}
